import React from "react";

import { Age } from "~/components/age";
import { IconExternalLink } from "~/components/icon";
import { SkeletonText } from "~/components/skeleton/text";
import { Truncated } from "~/components/truncated";
import { assertNever } from "~/tools/assert-never";
import type { Time } from "~/tools/date-time/time";
import { formatUnits, toFormatterUnit } from "~/tools/formatter-unit";

import type { TimeRange } from "../../app/state/view/timeline/TimeRange";
import type {
	CellValue,
	ComponentLinkCell,
	DurationCell,
	HealthCell,
	LinkCell,
	MetricCell,
	MetricMetaDisplay,
	NumericCell,
	OverviewColumnMeta,
	ReadyStatusCell,
	TextCell,
} from "../../common/openapi";
import { calculateQueryRangeStep } from "../../metrics/utils";
import { LockedMetric } from "../_locked-metric";
import { HealthStateBadge } from "../badges/HealthStateBadge";
import { ComponentLink } from "../react/component-link/ComponentLink";
import { Link } from "../react/link";
import { ReadyStatusDisplay } from "../react/readyStatus/ReadyStatusDisplay";

import { MetricsChart } from "./_metrics-chart";
import { MetricsValue } from "./_metrics-value";
import { NotAvailable } from "./_not-available";

const METRICS_CHART_WIDTH_PX = 55;
const METRICS_CHART_HEIGHT_PX = 25;

function extractCellOfType<T extends CellValue["_type"]>(type: T, cell: undefined | CellValue) {
	return cell?._type === type ? (cell as Extract<CellValue, { _type: T }>) : undefined;
}

const CellText: React.FC<{
	cell: undefined | TextCell;
}> = ({ cell }) => (
	<SkeletonText when={cell ?? null}>{({ value }) => <Truncated className="max-w-60">{value}</Truncated>}</SkeletonText>
);

const CellDuration: React.FC<{
	cell: undefined | DurationCell;
	timestamp: undefined | Time;
}> = ({ cell, timestamp }) => (
	<SkeletonText when={cell ?? null}>
		{({ endDate, startDate }) => <Age end={endDate ?? timestamp} start={startDate} />}
	</SkeletonText>
);

const CellHealth: React.FC<{
	cell: undefined | HealthCell;
}> = ({ cell }) => <HealthStateBadge state={cell?.state ?? null} />;

const CellComponentLink: React.FC<{
	cell: undefined | ComponentLinkCell;
}> = ({ cell }) => (
	<SkeletonText when={cell ?? null}>
		{({ component }) => {
			if (component == null) {
				return <NotAvailable />;
			}

			return <ComponentLink componentIdentifier={component.componentId} text={component.name} />;
		}}
	</SkeletonText>
);

const CellLink: React.FC<{
	external: boolean;
	cell: undefined | LinkCell;
}> = ({ external, cell }) => (
	<SkeletonText when={cell ?? null}>
		{({ url, name }) => {
			if (external) {
				return (
					<a
						className="link link-secondary flex items-center gap-2"
						href={url}
						rel="noopener noreferrer"
						target="_blank"
					>
						<Truncated>{name}</Truncated>
						<IconExternalLink />
					</a>
				);
			}

			return (
				<Link theme="secondary" to={url}>
					<Truncated>{name}</Truncated>
				</Link>
			);
		}}
	</SkeletonText>
);

const CellNumeric: React.FC<{
	unit: undefined | string;
	cell: undefined | NumericCell;
}> = ({ unit, cell }) => (
	<SkeletonText when={cell}>
		{({ value }) => (value == null ? <NotAvailable /> : formatUnits(toFormatterUnit(unit), value))}
	</SkeletonText>
);

const CellReadyStatus: React.FC<{
	cell: undefined | ReadyStatusCell;
}> = ({ cell }) => (
	<SkeletonText when={cell ?? null}>
		{({ ready, total, status }) => {
			if (ready == null || total == null) {
				return <NotAvailable />;
			}

			return <ReadyStatusDisplay layout="ratio-indicator" ready={ready} readyStatus={status} total={total} />;
		}}
	</SkeletonText>
);

interface CellMetricProps {
	componentIdentifier: undefined | string;
	timestamp: undefined | Time;
	timeRange: undefined | TimeRange;
	columnId: string;
	title: string;
	display: MetricMetaDisplay;
	cell: undefined | MetricCell;
}

const CellMetric: React.FC<CellMetricProps> = ({
	componentIdentifier,
	timestamp,
	timeRange,
	columnId,
	title,
	display,
	cell,
}) => {
	if (display.locked) {
		return <LockedMetric size="sm" title={title} />;
	}

	const query = cell?.individualQuery;
	const step = timeRange == null ? 0 : calculateQueryRangeStep({ timeRange, chartWidth: METRICS_CHART_WIDTH_PX });
	const unit = toFormatterUnit(display.unit ?? undefined);

	return (
		<div className="flex items-center gap-3">
			{display.sparkline && (
				<MetricsChart
					batch={columnId}
					className="shrink-0"
					height={METRICS_CHART_HEIGHT_PX}
					query={query}
					step={step}
					timeRange={timeRange}
					timestamp={timestamp}
					width={METRICS_CHART_WIDTH_PX}
				/>
			)}

			<MetricsValue
				batch={columnId}
				boundMetricId={display.boundMetricId}
				className="skeleton:min-w-4"
				componentIdentifier={componentIdentifier}
				decimalPlaces={display.decimalPlaces ?? undefined}
				query={query}
				step={step}
				timeRange={timeRange}
				timestamp={timestamp}
				title={title}
				unit={unit}
			/>
		</div>
	);
};

interface CellProps {
	componentIdentifier: undefined | string;
	timestamp: undefined | Time;
	timeRange: undefined | TimeRange;
	col: undefined | OverviewColumnMeta;
	cell: undefined | CellValue;
}

export const Cell: React.FC<CellProps> = ({ componentIdentifier, timestamp, timeRange, col, cell }) => {
	if (col == null) {
		return <SkeletonText />;
	}

	switch (col.display._type) {
		case "TextMetaDisplay": {
			return <CellText cell={extractCellOfType("TextCell", cell)} />;
		}

		case "DurationMetaDisplay": {
			return <CellDuration cell={extractCellOfType("DurationCell", cell)} timestamp={timestamp} />;
		}

		case "HealthMetaDisplay": {
			return <CellHealth cell={extractCellOfType("HealthCell", cell)} />;
		}

		case "ComponentLinkMetaDisplay": {
			return <CellComponentLink cell={extractCellOfType("ComponentLinkCell", cell)} />;
		}

		case "LinkMetaDisplay": {
			return <CellLink cell={extractCellOfType("LinkCell", cell)} external={col.display.external} />;
		}

		case "NumericMetaDisplay": {
			return <CellNumeric cell={extractCellOfType("NumericCell", cell)} unit={col.display.unit ?? undefined} />;
		}

		case "ReadyStatusMetaDisplay": {
			return <CellReadyStatus cell={extractCellOfType("ReadyStatusCell", cell)} />;
		}

		case "MetricMetaDisplay": {
			return (
				<CellMetric
					cell={extractCellOfType("MetricCell", cell)}
					columnId={col.columnId}
					componentIdentifier={componentIdentifier}
					display={col.display}
					timeRange={timeRange}
					timestamp={timestamp}
					title={col.title}
				/>
			);
		}

		default: {
			return assertNever(col.display);
		}
	}
};
